import React, { Component } from "react";
import { Switch, Route, Redirect } from "react-router";
import { BrowserRouter } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import RootRedirect from "../RootRedirect";
import ForcePasswordChangeGuard from "../ForcePasswordChangeGuard";
import NotFound from "../NotFound";
import ChangePassword from "../ChangePassword";
import ForcePassword from "../ForcePassword";
import Dashboard from "../Dashboard";
import VideoCall from "../VideoCalls/VideoCall";
import ClinicianDetail from "../ClinicianDetail";
import ClinicianSessions from "../ClinicianSessions";
import ClinicianClients from "../ClinicianClients";
import CustomerVideoCalls from "../ClientDetails/CustomerVideoCalls";
import Clinicians from "../Clinicians";
import Users from "../Users";
import User from "../Users/User";
import VideoCallSummary from "../VideoCallSummary";
import Chats from "../Chats";
import ChatReview from "../Chats/Chat";
import ReadOnlyChatChime from "../ChimeChat/ReadOnlyChimeChat";
import SavedForLaterCustomers from "../SavedForLaterCustomers";
import VideoSessionNote from "../VideoCalls/VideoSessionNote";
import ErrorBoundary from "../ErrorBoundary";
import ClientDetails from "../ClientDetails";
import WebrtcTest from "../WebrtcTest";
import CallInstructions from "../CallInstructions";
import CaregiverSessions from "../CaregiverSessions";
import CaregiverPastSessions from "../CaregiverSessions/PastSessions";
import InsurancePlan from "../AnLibraries/Insurance/InsuranceUtility/InsurancePlan";
import InsuranceProvider from "../AnLibraries/Insurance/InsuranceUtility/InsuranceProvider";
import BillingItems from "../BillingItems";
import EventScheduling from "components/EventScheduling";
import Messages from "components/Messages";
import IntercomAiController from "components/IntercomAi/IntercomAiController";
import { GlobalToastProvider } from "components/GlobalToastProvider";
import { GlobalPageLoadingProvider } from "components/GlobalPageLoadingProvider";
import { GlobalMiraProvider } from "components/GlobalMiraProvider";
import VideoPlayer from "components/VideoPlayer";
import AccountSelectionSplashScreen from "components/AccountSelectionSplashScreen";
import AcceptInvitation from "components/AcceptInvitation";
import Matching from "components/Matching";
import DocumentViewer from "../DocumentViewer";
import AnLibraries from "../AnLibraries";
import LogRocket from "logrocket";
import { miraNetworkSanitizer } from "utils/miraNetworkSanitizer";
import { getUserId } from "selectors";
import { connect } from "react-redux";
import Rescheduling from "components/Rescheduling";
import { LayoutProvider } from "../../layouts/LayoutProvider";
import { MainLayout } from "../../layouts/MainLayout";

const ExperimentLaunchPage = React.lazy(() => import("../Experiments/ExperimentLaunchPage"));

if (process.env.LOG_ROCKET_APP_ID) {
  LogRocket.init(process.env.LOG_ROCKET_APP_ID, {
    network: {
      requestSanitizer: miraNetworkSanitizer,
      responseSanitizer: miraNetworkSanitizer,
    },
  });
} else {
  console.warn("LOG_ROCKET_APP_ID is undefined. Log Rocket cannot be initialized.");
}

const mapStateToProps = (state) => {
  return {
    userId: getUserId(state),
  };
};

class Layout extends Component {
  render() {
    const { userId } = this.props;
    if (userId) {
      LogRocket.identify(userId);
    }

    return (
      <BrowserRouter>
        <CssBaseline />
        <ForcePasswordChangeGuard />
        <IntercomAiController />
        {/* Unread assessments each keep a persistent toast; 5 leaves room for transient ones too. */}
        <GlobalToastProvider maxToasts={5}>
          <GlobalMiraProvider>
            <LayoutProvider>
              <Switch>
                {/* Standalone experiment launch route: opens the lab in a new tab (Experiments tab) */}
                <Route
                  path="/experiments/:experimentId"
                  exact
                  render={() => (
                    <React.Suspense fallback={null}>
                      <ExperimentLaunchPage />
                    </React.Suspense>
                  )}
                />

                {/* Main App Route */}
                <Route path="/">
                  <MainLayout>
                    <ErrorBoundary>
                      <GlobalPageLoadingProvider>
                        <Switch>
                          <Route path="/" exact component={RootRedirect} />
                          <Route
                            path="/account-selection"
                        exact
                        component={AccountSelectionSplashScreen}
                      />
                      <Route path="/dashboard" exact component={Dashboard} />
                      <Route path="/account" exact>
                        <Redirect to="/account/profile" />
                      </Route>
                      <Route path="/account/:section" exact component={ClientDetails} />
                      <Route path="/sessions" exact component={CaregiverSessions} />
                      <Route path="/all-sessions" exact component={CaregiverPastSessions} />
                      <Route path="/change-password" exact component={ChangePassword} />
                      <Route path="/force-password-change" exact component={ForcePassword} />
                      {/* Primary setup (invitation) path. an-api sets the password and
                          establishes the session cookie server-side; this app and the
                          API share a registrable domain so the cookie is valid here.
                          The auth portal also hosts an equivalent /setup/:guid screen
                          for any links already sent pointing there. */}
                      <Route path="/setup/:guid" exact component={ForcePassword} />
                      <Route path="/accept-invitation/:guid" exact component={AcceptInvitation} />
                      <Route
                        path="/stream/:videoKey"
                        exact
                        render={(props) => {
                          return (
                            <VideoPlayer videoKey={props.match.params.videoKey} {...props} />
                          );
                        }}
                      />
                      <Route path="/clinician-schedule" exact component={ClinicianSessions} />
                      <Route
                        path="/clinician-schedule/create-event"
                        exact
                        component={EventScheduling}
                      />
                      <Route path="/clients" exact component={ClinicianClients} />
                      <Route
                        path="/video-calls/user/:clientId"
                        exact
                        component={CustomerVideoCalls}
                      />
                      <Route path="/video-calls/:id" exact component={VideoCall} />
                      <Route path="/session-notes/:id" exact component={VideoSessionNote} />

                      <Route
                        path="/my-info/:section?"
                        render={(props) => <ClinicianDetail key={"clinician_profile"} {...props} />}
                      />
                      <Route
                        path="/clients/:id/:section/:slug?/:subSection?/:subSlug?"
                        component={ClientDetails}
                      />
                      <Route path="/clinicians" exact component={Clinicians} />
                      <Route
                        path="/clinicians/:id/:section?"
                        exact
                        render={(props) => (
                          <ClinicianDetail
                            key={props?.match?.params?.id || "clinician_detail"}
                            {...props}
                          />
                        )}
                      />
                      <Route path="/users" exact component={Users} />
                      <Route path="/users/:id" exact component={User} />
                      <Route path="/video-call-summary" exact component={VideoCallSummary} />
                      <Route path="/chats" exact component={Chats} />
                      <Route path="/chats/:id" exact component={ChatReview} />
                      <Route path="/chat/:id" exact component={ReadOnlyChatChime} />
                      <Route path="/saved-customers" exact component={SavedForLaterCustomers} />
                      <Route path="/test" exact component={WebrtcTest} />
                      <Route path="/call-instructions/:id" exact component={CallInstructions} />
                      <Route path="/session-details/:id" exact component={CallInstructions} />
                      <Route path="/libraries" exact component={AnLibraries} />
                      <Route path="/libraries/insurance/:id" exact component={InsurancePlan} />
                      <Route
                        path="/libraries/insurance-provider/:id"
                        exact
                        component={InsuranceProvider}
                      />
                      <Route path="/billing" exact component={BillingItems} />
                      <Route path="/messages" exact component={Messages} />
                      <Route path="/matching" exact component={Matching} />
                      <Route
                        path="/document-viewer/:userId/:documentId"
                        component={DocumentViewer}
                      />
                      <Route path="/rescheduling-session/:id/:step?" component={Rescheduling} />
                      <Route path="/not-found" exact component={NotFound} />
                      <Route path="*" component={NotFound} />
                    </Switch>
                  </GlobalPageLoadingProvider>
                </ErrorBoundary>
              </MainLayout>
                </Route>
              </Switch>
            </LayoutProvider>
          </GlobalMiraProvider>
        </GlobalToastProvider>
      </BrowserRouter>
    );
  }
}

export default connect(mapStateToProps)(Layout);
